import { coordEq } from "@dailywars/core/engine";
import type { Coord, GameState, Move, UnitKind } from "@dailywars/core/engine";
import { createStore, type StoreApi } from "zustand/vanilla";

import { UNMOVED_HIGHLIGHT_FADE_MS, UNMOVED_HIGHLIGHT_HOLD_MS } from "#/game/animation/constants";
import { cycleUnmovedUnitSelection } from "#/game/selection/cycleUnmovedUnit";
import { handleTileSelect } from "#/game/selection/intents";
import { cancelSelection, cancelConfirmAttack } from "#/game/selection/transitions";
import {
  applyMoveAction,
  beginPlaybackAfterSubmit,
  commitAttack,
  completeAnimation,
  playNextStep,
  setFastPlayback as applyFastPlayback,
  undoLastMove,
} from "#/game/store/gameMutations";
import { selectedUnitIdFromSelection, unactedOwnUnitIdsFromState } from "#/game/store/selectors";
import type { GameSettings, StoreState } from "#/game/store/types";

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  fastPlayback: false,
  endTurnConfirmation: true,
  disableAiPlayback: false,
};

function buildInitialState({
  initialState,
  initialServerDayNumber,
  settings,
}: {
  initialState: GameState;
  initialServerDayNumber: number;
  settings: GameSettings;
}): StoreState {
  return {
    fastPlayback: settings.fastPlayback,
    endTurnConfirmation: settings.endTurnConfirmation,
    disableAiPlayback: settings.disableAiPlayback,
    serverState: initialState,
    serverDayNumber: initialServerDayNumber,
    localState: initialState,
    pendingMoves: [],
    lastCycledUnitId: null,
    selection: { mode: "none" },
    animation: null,
    animationQueue: [],
    previewAnimation: null,
    isSubmitting: false,
    aiPlayback: null,
    hoveredTile: null,
    unmovedHighlight: null,
    scrollFocusTile: null,
  };
}

type GameActions = {
  selectTile: (tile: Coord) => void;
  setHoveredTile: (tile: Coord | null) => void;
  completeAnimation: () => void;
  playbackTick: () => void;
  cancelAttack: () => void;
  cancelSelection: () => void;
  chooseBuild: (pos: Coord, unitKind: UnitKind) => void;
  confirmAttack: () => void;
  undoLastMove: () => void;
  cycleUnmovedUnit: () => void;
  showUnmovedHighlight: () => void;
  startUnmovedHighlightFade: () => void;
  clearUnmovedHighlight: () => void;
  submitStart: () => void;
  submitFail: () => void;
  submitFinish: (input: {
    finalState: GameState;
    aiMoves: Move[];
    finalServerDayNumber: number;
    includePlayerEndTurnPlayback?: boolean;
  }) => void;
  setFastPlayback: (fastPlayback: boolean) => void;
  setEndTurnConfirmation: (endTurnConfirmation: boolean) => void;
  setDisableAiPlayback: (disableAiPlayback: boolean) => void;
};

export type GameStore = StoreState & GameActions;
export type GameStoreApi = StoreApi<GameStore>;

type SetState = GameStoreApi["setState"];
type GetState = GameStoreApi["getState"];

function unmovedHighlightActions(set: SetState, get: GetState) {
  return {
    showUnmovedHighlight: () => {
      const unitIds = unactedOwnUnitIdsFromState(get());
      if (unitIds.length === 0) {
        set({ unmovedHighlight: null });
        return;
      }
      set({ unmovedHighlight: { unitIds, fadeStartedAtMs: null } });
    },
    startUnmovedHighlightFade: () => {
      const current = get().unmovedHighlight;
      if (!current) return;
      const fadeStartedAtMs = typeof performance !== "undefined" ? performance.now() : 0;
      set({ unmovedHighlight: { ...current, fadeStartedAtMs } });
      window.setTimeout(() => {
        const highlight = get().unmovedHighlight;
        if (highlight?.fadeStartedAtMs === fadeStartedAtMs) {
          set({ unmovedHighlight: null });
        }
      }, UNMOVED_HIGHLIGHT_HOLD_MS + UNMOVED_HIGHLIGHT_FADE_MS);
    },
    clearUnmovedHighlight: () => {
      if (get().unmovedHighlight) set({ unmovedHighlight: null });
    },
  };
}

function gameplayActions(set: SetState, get: GetState): Omit<GameActions, keyof SubmitActions> {
  return {
    selectTile: (tile) => {
      const next = handleTileSelect(get(), tile);
      const selectingUnit = selectedUnitIdFromSelection(next.selection) !== null;
      set({
        ...next,
        scrollFocusTile: null,
        hoveredTile: tile,
        ...(selectingUnit ? { unmovedHighlight: null } : {}),
      });
    },
    setHoveredTile: (tile) => {
      const s = get();
      if (s.hoveredTile === tile) return;
      if (s.hoveredTile && tile && coordEq(s.hoveredTile, tile)) return;
      set({ hoveredTile: tile });
    },
    completeAnimation: () => set(completeAnimation(get())),
    playbackTick: () => {
      const s = get();
      if (!s.aiPlayback || s.animation) return;
      set(playNextStep({ state: s, playback: s.aiPlayback }));
    },
    cancelAttack: () => set(cancelConfirmAttack(get())),
    cancelSelection: () => set({ ...cancelSelection(get()), scrollFocusTile: null }),
    chooseBuild: (pos, unitKind) => {
      const s = get();
      if (s.selection.mode !== "building") return;
      set(applyMoveAction({ state: s, move: { type: "build", pos, unitKind } }));
    },
    confirmAttack: () => set(commitAttack(get())),
    undoLastMove: () => set(undoLastMove(get())),
    cycleUnmovedUnit: () => set({ ...cycleUnmovedUnitSelection(get()), unmovedHighlight: null }),
    ...unmovedHighlightActions(set, get),
    setFastPlayback: (fastPlayback) => set(applyFastPlayback({ state: get(), fastPlayback })),
    setEndTurnConfirmation: (endTurnConfirmation) => set({ endTurnConfirmation }),
    setDisableAiPlayback: (disableAiPlayback) => set({ disableAiPlayback }),
  };
}

type SubmitActions = Pick<GameActions, "submitStart" | "submitFail" | "submitFinish">;

function submitActions(set: SetState, get: GetState): SubmitActions {
  return {
    submitStart: () =>
      set({
        isSubmitting: true,
        selection: { mode: "none" },
        scrollFocusTile: null,
        unmovedHighlight: null,
      }),
    submitFail: () => {
      const s = get();
      set({
        isSubmitting: false,
        localState: s.serverState,
        pendingMoves: [],
        lastCycledUnitId: null,
        selection: { mode: "none" },
        animation: null,
        animationQueue: [],
        previewAnimation: null,
        aiPlayback: null,
        scrollFocusTile: null,
        unmovedHighlight: null,
      });
    },
    submitFinish: (input) =>
      set(
        beginPlaybackAfterSubmit({
          state: get(),
          finalState: input.finalState,
          aiMoves: input.aiMoves,
          finalServerDayNumber: input.finalServerDayNumber,
          includePlayerEndTurnPlayback: input.includePlayerEndTurnPlayback ?? true,
        }),
      ),
  };
}

export function createGameStore({
  initialState,
  initialServerDayNumber,
  settings = DEFAULT_GAME_SETTINGS,
}: {
  initialState: GameState;
  initialServerDayNumber: number;
  settings?: GameSettings;
}): GameStoreApi {
  return createStore<GameStore>((set, get) => ({
    ...buildInitialState({ initialState, initialServerDayNumber, settings }),
    ...gameplayActions(set, get),
    ...submitActions(set, get),
  }));
}
